import React from 'react';
import { X, Clock, AlertCircle, Check, CheckCircle } from 'lucide-react';

const ViewDraftQuestionModal = ({ isOpen, onClose, question }) => {
  if (!isOpen || !question) return null;

  const getStatusInfo = (status) => {
    switch (status) {
      case 'DRAFT':
        return { icon: Clock, color: 'text-gray-600', bg: 'bg-gray-100', label: 'Draft' };
      case 'NEEDS_WORK':
        return { icon: AlertCircle, color: 'text-yellow-600', bg: 'bg-yellow-100', label: 'Needs Work' };
      case 'APPROVED':
        return { icon: Check, color: 'text-green-600', bg: 'bg-green-100', label: 'Approved' };
      case 'PUBLISHED':
        return { icon: CheckCircle, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Published' };
      default:
        return { icon: Clock, color: 'text-gray-600', bg: 'bg-gray-100', label: status };
    }
  };

  const statusInfo = getStatusInfo(question.status);
  const StatusIcon = statusInfo.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <h2 className="text-xl font-semibold text-gray-900">View Draft Question</h2>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.bg} ${statusInfo.color}`}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {statusInfo.label}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Question Text */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Question</h3>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-800 whitespace-pre-wrap">{question.questionText}</p>
            </div>
          </div>

          {/* Options */}
          {question.options && question.options.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Answer Options</h3>
              <div className="space-y-2">
                {question.options.map((option, index) => (
                  <div
                    key={index}
                    className={`flex items-center space-x-3 p-3 rounded-lg border ${
                      option.isCorrect
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
                      option.isCorrect
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="flex-1 text-gray-800">{option.text}</span>
                    {option.isCorrect && (
                      <Check className="h-4 w-4 text-green-600" />
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Explanation */}
          {question.explanation && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Explanation</h3>
              <div className="bg-blue-50 rounded-lg p-4">
                <p className="text-gray-800 whitespace-pre-wrap">{question.explanation}</p>
              </div>
            </div>
          )}

          {/* Question Details */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Question Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">Subject & Topic</h4>
                <p className="text-gray-800">{question.subject}</p>
                {question.topic && (
                  <p className="text-gray-600 text-sm">{question.topic}</p>
                )}
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">Difficulty</h4>
                <p className="text-gray-800">{question.difficultyLevel}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">Question Type</h4>
                <p className="text-gray-800">{question.questionType || 'TEXT_LATEX'}</p>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">Question Type</h4>
                <p className="text-gray-800">{question.questionType || question.type || 'MCQ_SINGLE_CORRECT'}</p>
              </div>
            </div>
          </div>

          {/* Review Information */}
          {(question.reviewComments || question.reviewedBy) && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Review Information</h3>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                {question.reviewedBy && (
                  <div className="mb-2">
                    <span className="font-medium text-gray-700">Reviewed by:</span>
                    <span className="ml-2 text-gray-800">{question.reviewedBy}</span>
                  </div>
                )}
                {question.reviewedAt && (
                  <div className="mb-2">
                    <span className="font-medium text-gray-700">Reviewed on:</span>
                    <span className="ml-2 text-gray-800">
                      {new Date(question.reviewedAt).toLocaleDateString()}
                    </span>
                  </div>
                )}
                {question.reviewComments && (
                  <div>
                    <span className="font-medium text-gray-700">Comments:</span>
                    <p className="mt-1 text-gray-800 whitespace-pre-wrap">{question.reviewComments}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-3">Metadata</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-700">Created by:</span>
                <span className="ml-2 text-gray-800">{question.createdBy}</span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Created on:</span>
                <span className="ml-2 text-gray-800">
                  {new Date(question.createdAt).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span className="font-medium text-gray-700">Last updated:</span>
                <span className="ml-2 text-gray-800">
                  {new Date(question.updatedAt).toLocaleDateString()}
                </span>
              </div>
              {question.publishedAt && (
                <div>
                  <span className="font-medium text-gray-700">Published on:</span>
                  <span className="ml-2 text-gray-800">
                    {new Date(question.publishedAt).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Tags */}
          {question.tags && question.tags.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Tags</h3>
              <div className="flex flex-wrap gap-2">
                {question.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewDraftQuestionModal;
